import { useMemo } from 'react';
import { useSearch } from '@queries/fetchSearch';

import { MIN_QUERY_LENGTH, PALETTE_RESULTS_SIZE } from '../constants';
import type { SearchResultType, SearchSort } from '../types';

export function useCommandPaletteSearch({
  query,
  debouncedQuery,
  type,
  sort,
  enabled,
  hasQuery,
}: {
  query: string;
  debouncedQuery: string;
  type?: SearchResultType;
  sort: SearchSort;
  enabled: boolean;
  hasQuery: boolean;
}) {
  const {
    data: searchData,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    status,
    error,
  } = useSearch({
    search: debouncedQuery,
    type,
    sort,
    size: PALETTE_RESULTS_SIZE,
    enabled: enabled && hasQuery,
  });

  const searchResults = useMemo(() => searchData?.pages?.flatMap((page) => page.searchResults) ?? [], [searchData]);
  const totalDBRowCount = searchData?.pages[0]?.total ?? 0;
  const totalFetched = searchResults.length;

  return {
    searchResults: hasQuery ? (searchResults ?? []) : [],
    total: hasQuery ? totalDBRowCount : 0,
    isFetching,
    isError,
    error,
    hasQuery,
    fetchNextPage,
    hasNextPage,
    status,
    totalFetched,
    isFetchingNextPage,
    // true while the user has typed ahead of the debounce or a fetch is in flight
    isPending: query.trim().length >= MIN_QUERY_LENGTH && (query.trim() !== debouncedQuery || isFetching),
  };
}
